/**
 * The Billing tab's small pieces (`docs/visits-billing-and-proposal.md` §8.2,
 * Board 12): the `brow` — a label column 88 wide and the controls wrapping
 * beside it, a hint under them — the Reads band every panel ends in, the
 * `%` | `$` control the deposit and every stage share, and a text box that
 * commits when it is left.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { BILLING_TAB, type BillingPlanKind } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { controlClass } from '../../components/form/field.js'
import { Segmented } from '../../components/primitives.js'
import { IconCheck, IconClock, IconEye, IconLayers, IconLoop } from '../../frame/icons.js'

/**
 * One row of a kind's controls. On a phone (`stack`) the label sits above and
 * the controls take the width, which is Board 16's arrangement of the same
 * fields.
 */
export function Row({
  label, hint, stack, children,
}: { label: string; hint?: ReactNode; stack?: boolean; children: ReactNode }) {
  if (stack) {
    return (
      <div className="flex flex-col gap-1.5" data-billing-row="">
        <span className="text-[12.5px] text-text-muted">{label}</span>
        <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
        {hint && <span className="text-[11.5px] leading-[1.4] text-text-faint">{hint}</span>}
      </div>
    )
  }
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-[5px]" data-billing-row="">
      <span className="text-[12px] text-text-muted" data-billing-label="">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
      {hint && (
        <span className="col-start-2 -mt-[3px] text-[11.5px] leading-[1.4] text-text-faint">{hint}</span>
      )}
    </div>
  )
}

/** Faint words between controls: `April – October, the months the visits run`. */
export function Said({ children, tone = 'faint' }: { children: ReactNode; tone?: 'faint' | 'muted' }) {
  return (
    <span className={cn('text-[12.5px]', tone === 'faint' ? 'text-text-faint' : 'text-text-muted')}>
      {children}
    </span>
  )
}

/** An accent text button: `Change months`, `+ Add a ceiling`, `Add a stage`. */
export function LinkButton({
  children, onClick, disabled, tone = 'accent',
}: { children: ReactNode; onClick: () => void; disabled?: boolean; tone?: 'accent' | 'faint' }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn('inline-flex items-center gap-[5px] text-[12.5px] font-semibold',
        tone === 'accent' ? 'text-accent hover:text-accent-dark' : 'text-text-faint hover:text-text-muted')}
    >
      {children}
    </button>
  )
}

/** The band every kind's panel ends in: `Reads:` and, under it, `On the proposal:`. */
export function ReadsBand({ reads, line }: { reads: string; line: string }) {
  return (
    <div
      data-reads=""
      className="flex gap-[9px] rounded-[10px] border border-accent-line bg-accent-soft px-3 py-[9px]
                 text-[12.5px] leading-[1.4] text-accent-dark"
    >
      <span className="mt-px shrink-0"><IconEye size={14} /></span>
      <span className="min-w-0">
        <b className="font-semibold">{BILLING_TAB.reads}</b> {reads}
        {line && (
          <span className="mt-[3px] block text-[12px] text-text-muted">
            {BILLING_TAB.onTheProposal} {line}
          </span>
        )}
      </span>
    </div>
  )
}

/** The `%` | `$` control (§8.2): the deposit's and every stage's, one component. */
export function UnitToggle({
  unit, onChange, width, label,
}: { unit: 'pct' | 'flat'; onChange: (unit: 'pct' | 'flat') => void; width: number; label: string }) {
  return (
    <span className="inline-flex shrink-0" style={{ width }} data-stage-unit={unit}>
      <Segmented
        size="field"
        label={label}
        value={unit}
        onChange={onChange}
        options={[{ value: 'pct', label: '%' }, { value: 'flat', label: '$' }]}
      />
    </span>
  )
}

/** A text box that commits when it is left or Enter is pressed — a stage's name. */
export function TextBox({
  value, onCommit, label, width, disabled,
}: { value: string; onCommit: (next: string) => void; label: string; width?: string; disabled?: boolean }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => { setDraft(value) }, [value])
  return (
    <input
      value={draft}
      aria-label={label}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        const next = draft.trim()
        if (!next) { setDraft(value); return }
        if (next !== value) onCommit(next)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') { setDraft(value); event.currentTarget.blur() }
      }}
      className={controlClass({ width: width ?? 'w-full', disabled })}
    />
  )
}

/** The kind cards' glyphs (Board 11): ✓ · repeat · layers · $ · clock. */
export function KindGlyph({ kind, size = 15 }: { kind: BillingPlanKind; size?: number }) {
  switch (kind) {
    case 'completion': return <IconCheck size={size} />
    case 'period': return <IconLoop size={size} />
    case 'milestones': return <IconLayers size={size} />
    case 'time_materials': return <IconClock size={size} />
    default: return <span className="text-[14px] font-bold leading-none">$</span>
  }
}

/** The light chip beside a plan's name: `1 visit`, `2 visits`. */
export function CountChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-[6px] bg-line-soft
                     px-[7px] text-[11px] font-semibold text-text-muted">
      {children}
    </span>
  )
}

/** The `$` tile a plan card starts with (34 on a desktop, Board 11). */
export function MoneyTile({ size = 34 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 items-center justify-center rounded-[10px] border border-accent-line
                 bg-accent-soft text-[15px] font-bold text-accent-dark"
      style={{ width: size, height: size }}
    >
      $
    </span>
  )
}

/**
 * The day of the month, 62 wide (Board 12): reads `1st` and takes a number
 * when it is typed into, held to 1–28 so every month has the day.
 */
export function DayBox({
  day, onCommit, label, disabled,
}: { day: number; onCommit: (day: number) => void; label: string; disabled?: boolean }) {
  const [draft, setDraft] = useState<string | null>(null)
  const said = (n: number) => {
    const tail = n % 100 >= 11 && n % 100 <= 13 ? 'th'
      : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th'
    return `${n}${tail}`
  }
  return (
    <input
      aria-label={label}
      inputMode="numeric"
      disabled={disabled}
      value={draft ?? said(day)}
      onFocus={() => setDraft(String(day))}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        const next = Math.round(Number(draft))
        setDraft(null)
        if (Number.isFinite(next) && next >= 1 && next !== day) onCommit(Math.min(28, next))
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') { setDraft(null); event.currentTarget.blur() }
      }}
      className={cn(controlClass({ width: 'w-[62px]', disabled }), 'text-right')}
    />
  )
}
